import { Button } from "@/lib/components/Button/Button";
import { useReferralOverviewQuery } from "@/lib/queries/referral-queries";
import { IconCopy, IconGift } from "@tabler/icons-react";
import { useParams } from "react-router";
import Toast from "@/lib/components/Toast";
import { getEnv } from "@/lib/utils";
import { AnalyticsApi } from "@/lib/api/analytics-api";

type Props = {
  onShowEarnedCredits: () => void;
};

export default function ReferralInviteView({ onShowEarnedCredits }: Props) {
  const { teamExternalId } = useParams();
  const { data: referralOverview, status } = useReferralOverviewQuery({
    teamExternalId,
  });

  const inviteLink = `${getEnv("VITE_AMPLIFILES_REFERRAL_PAGE")}?c=${referralOverview?.referralCode}`;

  const copyToClipboard = async () => {
    if (!referralOverview?.referralCode) {
      Toast.error("Invite link is not loaded yet, please try again.");
      return;
    }

    try {
      await navigator.clipboard.writeText(`https://${inviteLink}`);
      Toast.info("Invite link copied to clipboard!");
      AnalyticsApi.logReferralLinkCopied(teamExternalId);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      Toast.error("Failed to copy invite link");
    }
  };

  let inviteLinkText = "Loading invite link...";
  let inviteLinkClass = "text-text-primary";

  if (status === "success") {
    inviteLinkText = inviteLink;
  } else if (status === "error") {
    inviteLinkText = "Failed to load invite link";
    inviteLinkClass = "text-error";
  }

  return (
    <div className="w-full px-4 pb-6 md:px-6">
      <h2 className="mt-6 mb-3 text-lg font-semibold md:text-xl">
        Invite your friends and get rewarded
      </h2>
      <p>
        Your friend gets 1,200 free credits when they sign up. You earn 20% of
        their payments in credits, up to $100 per friend. The credits are added
        to your account automatically. The more friends you invite, the more you
        earn.
      </p>
      <div className="mt-5 mb-3 flex items-center gap-1 overflow-hidden rounded-xl border border-border py-4 pr-4 pl-6">
        <IconGift className="h-5 w-5 text-tertiary-400" />
        <div className="mx-3 w-full flex-1 overflow-hidden">
          <p className="text-xs font-medium text-text-secondary">Invite link</p>
          <p className={`truncate text-sm font-medium ${inviteLinkClass}`}>
            {inviteLinkText}
          </p>
        </div>
        <Button
          className="shrink-0 basis-auto"
          variant="ghost"
          size="icon"
          onClick={copyToClipboard}
        >
          <IconCopy />
        </Button>
      </div>
      <Button
        variant="tertiary"
        className="w-full outline-primary-400"
        onClick={copyToClipboard}
      >
        Copy invite link
      </Button>
      <div className="mt-4 flex items-center justify-center gap-6 text-xs text-text-secondary">
        <button
          type="button"
          className="cursor-pointer"
          onClick={onShowEarnedCredits}
        >
          Earned credits
        </button>
        <a
          href="https://www.amplifiles.ai/referral-program-terms-of-service"
          target="_blank"
        >
          Referral program terms
        </a>
      </div>
    </div>
  );
}
